// src/sim/hiding/spots.ts — P3 hide-candidate query (HIDE-01..03, D-19..D-22, UI-SPEC).
//
// Invariants:
//   - Pure: no side effects; input entity/profile/mapData/occupied never mutated.
//   - Hysteresis: enter at `P3_HIDING_SPOT_ACTIVATION_RADIUS_PX` (32 px), exit at
//     `P3_HIDING_SPOT_HYSTERESIS_PX` (40 px). The 8 px deadband prevents the HUD
//     context button from flickering when the player grazes the enter boundary.
//   - Filters AND-composed in a single pass: occupancy → size → requiredAbilities
//     → forbiddenCharacters → threshold. Any failure skips the spot outright.
//   - Deterministic tie-break: equal squared distances are resolved by lowest
//     `spot.id` via `String.localeCompare` — keeps replay/debug output stable
//     when the authored map happens to place two candidates equidistant from
//     the player.
//
// Source: .planning/phases/03-core-sim-primitives/03-CONTEXT.md D-19..D-22
//         + .planning/phases/03-core-sim-primitives/03-UI-SPEC.md §HUD context-button hysteresis.

import type { Entity } from '../../shared/types/world';
import type { HidingSpotAuth, MapData } from '../../shared/types/map';
import type { CharacterProfile } from '../characters/character-profile';
import {
  P3_HIDING_SPOT_ACTIVATION_RADIUS_PX,
  P3_HIDING_SPOT_HYSTERESIS_PX,
} from './constants';

/**
 * Return the nearest filter-passing hiding spot within the activation radius
 * (or within the hysteresis radius if `previousActiveSpotId` matches). Returns
 * null when no candidate passes.
 *
 * Callers track `previousActiveSpotId` themselves (typically the hider's
 * currently-highlighted hide candidate, or the last candidate while the HUD
 * button is in its "armed" state).
 */
export function findActiveHidingSpot(
  entity: Entity,
  profile: CharacterProfile,
  mapData: MapData,
  occupiedSpotIds: Set<string>,
  previousActiveSpotId: string | null,
): HidingSpotAuth | null {
  const enterR2 = P3_HIDING_SPOT_ACTIVATION_RADIUS_PX * P3_HIDING_SPOT_ACTIVATION_RADIUS_PX;
  const exitR2 = P3_HIDING_SPOT_HYSTERESIS_PX * P3_HIDING_SPOT_HYSTERESIS_PX;

  let best: { spot: HidingSpotAuth; distSq: number } | null = null;

  for (const spot of mapData.hidingSpots) {
    // D-22 occupancy: an occupied spot is unavailable unless the same entity
    // is the current occupant (hence the `previousActiveSpotId` escape hatch).
    if (occupiedSpotIds.has(spot.id) && spot.id !== previousActiveSpotId) continue;

    // HIDE-02 size gate: large characters cannot use small-only spots.
    if (spot.size === 'small' && profile.size === 'large') continue;

    // D-19 ability gate: requiredAbilities must be a subset of profile.abilities.
    if (spot.requiredAbilities.length > 0) {
      let hasAll = true;
      for (const ability of spot.requiredAbilities) {
        if (!profile.abilities.includes(ability)) {
          hasAll = false;
          break;
        }
      }
      if (!hasAll) continue;
    }

    // D-19 per-character exclusion list.
    if (spot.forbiddenCharacters.includes(profile.id)) continue;

    const dx = spot.position.x - entity.position.x;
    const dy = spot.position.y - entity.position.y;
    const distSq = dx * dx + dy * dy;

    // Hysteresis: the previously-active spot uses the wider exit threshold; all
    // other spots (including newly-approached ones) use the narrower enter
    // threshold.
    const threshold = spot.id === previousActiveSpotId ? exitR2 : enterR2;
    if (distSq > threshold) continue;

    if (
      !best ||
      distSq < best.distSq ||
      (distSq === best.distSq && spot.id.localeCompare(best.spot.id) < 0)
    ) {
      best = { spot, distSq };
    }
  }

  return best?.spot ?? null;
}
